import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, RefreshCw, CalendarPlus, Mail } from "lucide-react";

export default function OwnerAdmins() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", restaurant_name: "", phone: "", password: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/owner/admins");
      setAdmins(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/owner/admins", form);
      toast.success("Administrador creado");
      setOpenCreate(false);
      setForm({ name: "", email: "", restaurant_name: "", phone: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const extend = async (id, days) => {
    try {
      await api.post(`/owner/admins/${id}/extend`, { days });
      toast.success(`Suscripción extendida +${days} días`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/owner/admins/${id}`);
      toast.success("Administrador eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const triggerCheck = async () => {
    try {
      await api.post("/owner/check-expirations");
      toast.success("Verificación completada");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="animate-fade-up" data-testid="owner-admins">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#7248ae] mb-2">Gestión</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-[#1a0f2e] tracking-tight">Administradores</h1>
          <p className="text-[#7248ae] mt-2">Da de alta nuevos restaurantes y administra sus suscripciones.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={triggerCheck} className="border-[#b182ec]/50" data-testid="check-expirations-button">
            <Mail className="h-4 w-4 mr-2" />Verificar vencidas
          </Button>
          <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogTrigger asChild>
              <Button className="bg-[#3e166c] hover:bg-[#7248ae] text-white rounded-xl" data-testid="new-admin-button">
                <Plus className="h-4 w-4 mr-2" />Nuevo administrador
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Nuevo administrador</DialogTitle>
              </DialogHeader>
              <form onSubmit={create} className="space-y-4" data-testid="new-admin-form">
                <div><Label>Nombre</Label><Input required value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="admin-name-input" /></div>
                <div><Label>Correo</Label><Input type="email" required value={form.email} onChange={e=>setForm({...form,email:e.target.value})} data-testid="admin-email-input" /></div>
                <div><Label>Nombre del restaurante</Label><Input required value={form.restaurant_name} onChange={e=>setForm({...form,restaurant_name:e.target.value})} data-testid="admin-restaurant-input" /></div>
                <div><Label>Celular</Label><Input required value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} data-testid="admin-phone-input" /></div>
                <div><Label>Contraseña</Label><Input type="password" required minLength={6} value={form.password} onChange={e=>setForm({...form,password:e.target.value})} data-testid="admin-password-input" /></div>
                <DialogFooter>
                  <Button type="submit" className="bg-[#3e166c] hover:bg-[#7248ae] text-white" data-testid="submit-admin-button">Crear</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <p className="text-[#7248ae]">Cargando…</p>
      ) : admins.length === 0 ? (
        <Card className="p-12 text-center border-dashed border-[#b182ec]/40">
          <p className="text-[#7248ae]">Aún no hay administradores. Crea el primero.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="admins-grid">
          {admins.map((a, idx) => (
            <Card key={a.id} className={`p-6 border border-[#b182ec]/30 rounded-2xl card-glow transition-all animate-fade-up stagger-${(idx%6)+1}`} data-testid={`admin-card-${a.email}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-display text-xl font-bold text-[#1a0f2e]">{a.name}</h3>
                  <p className="text-sm text-[#7248ae]">{a.email}</p>
                  <p className="text-sm text-[#7248ae]">{a.phone}</p>
                </div>
                <Badge
                  className={a.subscription_active ? "bg-[#10b981] hover:bg-[#10b981]/80" : "bg-[#e11d48] hover:bg-[#e11d48]/80"}
                  data-testid={`admin-status-${a.email}`}
                >
                  {a.subscription_active ? "Activo" : "Vencido"}
                </Badge>
              </div>
              <div className="mb-4 p-3 rounded-lg bg-[#3e166c]/5">
                <p className="text-xs uppercase tracking-wider text-[#7248ae]">Restaurante ID</p>
                <p className="text-sm text-[#1a0f2e] font-mono truncate">{a.restaurant_id}</p>
              </div>
              <div className="mb-5">
                <p className="text-xs uppercase tracking-wider text-[#7248ae] mb-1">Suscripción</p>
                <p className="text-sm text-[#1a0f2e]">
                  {a.subscription_expires_at ? formatDate(a.subscription_expires_at) : "Sin asignar"}
                </p>
                <p className="text-xs text-[#7248ae] mt-1">{a.subscription_active ? `${a.subscription_days_left} días restantes` : "Vencida"}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" onClick={() => extend(a.id, 30)} className="bg-[#3e166c] hover:bg-[#7248ae] text-white" data-testid={`extend-30-${a.email}`}>
                  <CalendarPlus className="h-4 w-4 mr-1" />+30 días
                </Button>
                <Button size="sm" variant="outline" onClick={() => extend(a.id, 7)} className="border-[#b182ec]/50" data-testid={`extend-7-${a.email}`}>
                  <RefreshCw className="h-4 w-4 mr-1" />+7 días
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="outline" className="border-[#e11d48]/40 text-[#e11d48] hover:bg-[#e11d48]/10 ml-auto" data-testid={`delete-admin-${a.email}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>¿Eliminar administrador?</AlertDialogTitle>
                      <AlertDialogDescription>Esta acción eliminará la cuenta de <strong>{a.name}</strong>. No se eliminará el restaurante ni los pedidos históricos.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction className="bg-[#e11d48] hover:bg-[#e11d48]/80" onClick={() => remove(a.id)} data-testid={`confirm-delete-${a.email}`}>Eliminar</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
